
import { Component, Mixins, Watch } from "vue-property-decorator";
import mixin from "./mixin";
import NoteFields from "@/components/widgets/NoteFields.vue";
import notifications from "../../../lib/notifications";
import {
  BulkOrderTimeframeResource,
  GetBulkOrderTimeframesRequest
} from "../../../models/api/orders/getBulkTimeframes";
import {
  SetBulkOrderTimeframesRequest,
  SetBulkOrderTimeframeResult
} from "../../../models/api/orders/setBulkTimeframes";
import moment from "moment";
import * as modals from "@/lib/modals";
import { getModule } from "vuex-module-decorators";
import { OrderModule } from "@/store/modules";

const orderModule = getModule(OrderModule);

@Component({
  name: modals.timeframeEntryModal,
  components: {
    NoteFields
  }
})
export default class TimeframeEntryModal extends Mixins(mixin) {
  timeframes: BulkOrderTimeframeResource[] = [];
  note: any = { text: null, visibleOption: "0", customerViewable: true, facilityViewable: true };
  loading: boolean = true;
  saving: boolean = false;

  async load() {
    this.show();

    const orderNumbers = this.selectedItems ? this.selectedItems.map(x => x.number) : [];
    if (orderNumbers.length > 0) {
      try {
        this.timeframes = await orderModule.getBulkOrderTimeframes({
          orderNumbers: orderNumbers
        });
        this.timeframes.forEach(timeframe => {
          timeframe.timeframeStart =
            moment(this.selectedItem.timeframeStart).format("HH:mm") != "00:00"
              ? moment(this.selectedItem.timeframeStart).format("HH:mm")
              : timeframe.defaultStartTime;
          timeframe.timeframeEnd =
            moment(this.selectedItem.timeframeEnd).format("HH:mm") != "00:00"
              ? moment(this.selectedItem.timeframeEnd).format("HH:mm")
              : moment(timeframe.defaultStartTime, "HH:mm")
                  .add(timeframe.timeframeWindowLength, "hour")
                  .format("HH:mm");
          this.$set(timeframe, "errors", []);
        });
      } catch (ex) {
        notifications.error("Timeframes", "Failed to load timeframes", ex);
      }
    }
    this.loading = false;
  }

  get deliveryForId() {
    if(!this.selectedItem)
      return 0;
      
    return this.selectedItem.deliveryForId ?? 0;
  }

  noteUpdated(newNote) {
    this.note = JSON.parse(JSON.stringify(newNote));
  }

  get invalidTimeframes() {
    return this.timeframes.filter(timeframe => timeframe.errors.length);
  }

  get isValid() {
    if (!this.timeframes) return false;

    return this.timeframes.every(x => {
      x.errors = [];
      const isStartTimeValid = this.validateStartTimeWithEarlyTime(x);
      const isStartEndComparisonValid = this.validateStartTimeWithEndTime(x);
      const isWindowlengthValid = this.validateWindowLength(x);
      if (!isStartTimeValid) {
        x.errors.push(
          `Timeframe Start Time cannot be earlier than Earliest Start Time (${this.minimumTimeForStart(
            x
          ).format("hh:mm A")})`
        );
      }
      if (!isStartEndComparisonValid) {
        x.errors.push("Timeframe Start Time cannot be later than Timeframe End Time");
      }
      if (!isWindowlengthValid) {
        x.errors.push(
          `Timeframe Start and End Times are invalid. Time must be at least ${x.timeframeWindowLength} hours apart`
        );
      }
      return isStartTimeValid && isStartEndComparisonValid && isWindowlengthValid;
    });
  }

  validateStartTimeWithEndTime(order) {
    if (!order.timeframeStart || !order.timeframeEnd) return false;
    const start = moment(order.timeframeStart, "HH:mm");
    const end = moment(order.timeframeEnd, "HH:mm");
    return start.isSameOrBefore(end);
  }

  validateStartTimeWithEarlyTime(order) {
    if (!order.timeframeStart) return false;
    const start = moment(order.timeframeStart, "HH:mm");
    const earliestStart = this.minimumTimeForStart(order);
    return start.isSameOrAfter(earliestStart);
  }

  validateWindowLength(timeframe) {
    const startTime = moment(timeframe.timeframeStart, "HH:mm");
    const endTime = moment(timeframe.timeframeEnd, "HH:mm");
    const duration = endTime.diff(startTime, "hours");
    return duration >= timeframe.timeframeWindowLength;
  }

  minimumTimeForStart(order) {
    return moment(order.earliestStart, "HH:mm");
  }

  minimumTimeForStartFormatted(order) {
    return order.minimumTimeForStart.format("HH:mm");
  }

  minimumTimeForEnd(order) {
    if (!order.timeframeStart) return null;

    if (!order.timeframeWindowLength) return null;

    let startTime = moment(order.timeframeStart, ["h:m a", "H:m"]).add(
      order.timeframeWindowLength,
      "hours"
    );
    return startTime;
  }

  minimumTimeForEndFormatted(order) {
    return this.minimumTimeForEnd(order) ? this.minimumTimeForEnd(order).format("HH:mm") : null;
  }

  async saveModal() {
    const orders = [];
    this.saving = true;
    this.timeframes.forEach(timeframe => {
      if (!timeframe.timeframeStart || !timeframe.timeframeEnd) return;

      orders.push({
        timeframeStart: timeframe.timeframeStart,
        timeframeEnd: timeframe.timeframeEnd,
        orderHeaderNumber: timeframe.orderHeaderNumber
      });
    });
    const request = { note: this.note, orders: orders };
    try {
      const results = await orderModule.setBulkOrderTimeframes(request);
      results.forEach(result => {
        this.addActionResult({
          number: result.orderHeaderNumber,
          success: result.successful,
          message: result.message
        });
      });
      this.hideModal();
    } catch (ex) {
      notifications.error("Set Timeframes", "Failed to set timeframes", ex);
    }

    this.refreshOrders();

    this.saving = false;
  }

  hide() {
    this.timeframes = [];
    this.loading = true;
  }
}
